// ─── Intercompany — periode-engine helpers (period-engine, spec 4 part B) ───
//
// Pure datum-helpers die een afrekentijdvak afleiden voor een IC-periode op
// basis van een datum + afrekenfrequentie. GEEN Prisma, GEEN node:* — direct
// unit-testbaar. Tijdvakken worden in Europe/Amsterdam geinterpreteerd
// (week = ma-zo ISO-8601) en als UTC-instant van de NL-wandklokgrens
// teruggegeven, conform CLAUDE.md §Tijdzone (DB = UTC, rekenen = NL-tijd).
//
// Hergebruikt:
//   • iso-week.ts          → mondayOfIsoWeek / isoWeeksInYear (ISO-kalender-math)
//   • amsterdam-tijd.ts     → amsterdamWallclockToUtc (DST-correcte NL→UTC-brug)
//
// Spec: docs/specs (ic-facturatiemoment-wekelijks-eenmalig) §2.

import type { ICAfrekenFrequentie } from '@streep/types'
import { mondayOfIsoWeek, isoWeeksInYear } from './iso-week'
import { amsterdamWallclockToUtc } from './amsterdam-tijd'

// ─── Intern: NL wall-clock datum-componenten van een UTC-instant ────────────

interface NlYmd {
  jaar: number
  maand: number // 1-12
  dag: number // 1-31
}

/**
 * Datum-componenten (jaar/maand/dag) van een UTC-instant, geinterpreteerd in
 * Europe/Amsterdam. Identiek patroon als `resolveHorizonMaanden` /
 * `utcToAmsterdamWallclock` — niet uit `process.tz` afleiden.
 */
function nlYmd(date: Date): NlYmd {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Amsterdam',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
  const parts = fmt.formatToParts(date)
  const get = (type: string): number => {
    const p = parts.find((x) => x.type === type)
    if (!p) throw new Error(`Intl formatToParts mist veld "${type}"`)
    return Number.parseInt(p.value, 10)
  }
  return { jaar: get('year'), maand: get('month'), dag: get('day') }
}

/** 'yyyy-mm-dd' uit losse componenten (nul-gevuld). */
function ymdString(jaar: number, maand: number, dag: number): string {
  const mm = String(maand).padStart(2, '0')
  const dd = String(dag).padStart(2, '0')
  return `${jaar}-${mm}-${dd}`
}

/**
 * UTC-instant van het begin van een NL-kalenderdag (00:00 Europe/Amsterdam).
 * Wrapper rond `amsterdamWallclockToUtc` voor leesbaarheid.
 */
function nlDagStartUtc(jaar: number, maand: number, dag: number): Date {
  return amsterdamWallclockToUtc(ymdString(jaar, maand, dag), '00:00')
}

/**
 * UTC-instant van het EINDE van een NL-kalenderdag: 23:59:59.999 NL-tijd.
 * We nemen 00:00 van de volgende dag en trekken 1 ms af, zodat DST-overgangen
 * (de dag waarop de klok wisselt) correct meelopen via de tweepass-solve.
 */
function nlDagEindUtc(jaar: number, maand: number, dag: number): Date {
  // De volgende dag uit een UTC-anker afleiden (Date.UTC normaliseert overflow),
  // dan terugvertalen naar NL-componenten en 00:00 NL daarvan nemen.
  const next = new Date(Date.UTC(jaar, maand - 1, dag + 1))
  const n = nlYmd(next)
  const volgendeDagStart = nlDagStartUtc(n.jaar, n.maand, n.dag)
  return new Date(volgendeDagStart.getTime() - 1)
}

// ─── Publieke types ─────────────────────────────────────────────────────────

export type ICPeriodetype = 'MAAND' | 'WEEK' | 'KWARTAAL'

export interface PeriodeRange {
  /** UTC-instant van het begin van het tijdvak (00:00 NL van de eerste dag). */
  van: Date
  /** UTC-instant van het einde van het tijdvak (23:59:59.999 NL van de laatste dag). */
  tot: Date
}

export interface IsoWeekResultaat {
  /** ISO-week-jaar (kan afwijken van het kalenderjaar rond de jaargrens). */
  jaar: number
  /** ISO-week 1-53. */
  week: number
}

export interface KwartaalResultaat {
  jaar: number
  /** 1-4. */
  kwartaal: number
}

export interface PeriodeVoorFrequentie {
  periodetype: ICPeriodetype
  jaar: number
  /** 1-12 bij MAAND, anders null. */
  maand: number | null
  /** ISO-week 1-53 bij WEEK, anders null. */
  week: number | null
  /** 1-4 bij KWARTAAL, anders null. */
  kwartaal: number | null
  periodeVan: Date
  periodeTot: Date
  /**
   * true alleen bij EENMALIG: de regel levert exact een transactie en herhaalt
   * niet. periodetype blijft MAAND (de maand van de datum) als drager.
   */
  eenmalig: boolean
}

// ─── ISO-week (Europe/Amsterdam) ─────────────────────────────────────────────

/**
 * ISO-8601 weeknummer + ISO-week-jaar van een datum, geinterpreteerd in
 * Europe/Amsterdam. Correct over de jaargrens: 2026-01-01 valt in ISO-week 1
 * van 2026, terwijl 2027-01-01 (een vrijdag) in week 53 van 2026 valt.
 *
 * Algoritme: ISO-8601 §4.3.2 — de week van een datum is de week die de
 * donderdag van diezelfde week bevat; het ISO-jaar is het jaar van die
 * donderdag. We rekenen op de NL-kalenderdag (00:00 NL als UTC-anker) zodat de
 * dag-van-de-week in NL-tijd klopt.
 */
export function isoWeek(date: Date): IsoWeekResultaat {
  const { jaar, maand, dag } = nlYmd(date)
  // Anker op de NL-kalenderdag als UTC-middernacht zodat getUTCDay() de juiste
  // weekdag in NL-tijd geeft (de wandklokdatum is hierboven al NL-afgeleid).
  const target = new Date(Date.UTC(jaar, maand - 1, dag))
  const dow = target.getUTCDay() || 7 // 1=ma..7=zo
  // Verschuif naar de donderdag van deze ISO-week.
  target.setUTCDate(target.getUTCDate() + 4 - dow)
  const isoJaar = target.getUTCFullYear()
  const jan1 = new Date(Date.UTC(isoJaar, 0, 1))
  const week = Math.ceil(((target.getTime() - jan1.getTime()) / 86400000 + 1) / 7)
  return { jaar: isoJaar, week }
}

/**
 * Tijdvak van een ISO-week: maandag 00:00 t/m zondag 23:59:59.999 in NL-tijd,
 * als UTC-instants. `mondayOfIsoWeek` levert de maandag-kalenderdatum (UTC);
 * we nemen daarvan de NL-componenten en bouwen de NL-dag-grenzen.
 */
export function weekRange(jaar: number, week: number): PeriodeRange {
  const maandag = mondayOfIsoWeek(week, jaar)
  // mondayOfIsoWeek geeft een UTC-middernacht; de Y/M/D ervan zijn de
  // kalenderdatum van de maandag. Die gebruiken we als NL-kalenderdag.
  const mJaar = maandag.getUTCFullYear()
  const mMaand = maandag.getUTCMonth() + 1
  const mDag = maandag.getUTCDate()
  const van = nlDagStartUtc(mJaar, mMaand, mDag)
  // Zondag = maandag + 6 dagen (kalenderdatum via UTC-anker, dan NL-eind).
  const zondag = new Date(Date.UTC(mJaar, mMaand - 1, mDag + 6))
  const tot = nlDagEindUtc(zondag.getUTCFullYear(), zondag.getUTCMonth() + 1, zondag.getUTCDate())
  return { van, tot }
}

// ─── Kwartaal (Europe/Amsterdam) ─────────────────────────────────────────────

/** Kwartaal (1-4) + jaar van een datum, in Europe/Amsterdam. */
export function kwartaalVan(date: Date): KwartaalResultaat {
  const { jaar, maand } = nlYmd(date)
  return { jaar, kwartaal: Math.floor((maand - 1) / 3) + 1 }
}

/**
 * Tijdvak van een kwartaal: eerste dag van de eerste maand 00:00 t/m laatste
 * dag van de derde maand 23:59:59.999, in NL-tijd als UTC-instants.
 */
export function kwartaalRange(jaar: number, kwartaal: number): PeriodeRange {
  const eersteMaand = (kwartaal - 1) * 3 + 1 // 1, 4, 7, 10
  const van = nlDagStartUtc(jaar, eersteMaand, 1)
  const laatsteMaand = eersteMaand + 2 // 3, 6, 9, 12
  // Laatste dag van de laatste maand: dag 0 van de maand erna (UTC-anker).
  const laatsteDagAnker = new Date(Date.UTC(jaar, laatsteMaand, 0))
  const tot = nlDagEindUtc(jaar, laatsteMaand, laatsteDagAnker.getUTCDate())
  return { van, tot }
}

// ─── Maand (Europe/Amsterdam) ────────────────────────────────────────────────

/**
 * Tijdvak van een maand: 1e 00:00 t/m laatste dag 23:59:59.999, in NL-tijd
 * als UTC-instants.
 */
export function maandRange(jaar: number, maand: number): PeriodeRange {
  const van = nlDagStartUtc(jaar, maand, 1)
  // Laatste dag van de maand: dag 0 van de maand erna (UTC-anker).
  const laatsteDagAnker = new Date(Date.UTC(jaar, maand, 0))
  const tot = nlDagEindUtc(jaar, maand, laatsteDagAnker.getUTCDate())
  return { van, tot }
}

// ─── Frequentie → periode-beslisser ──────────────────────────────────────────

/**
 * Bepaal het IC-periode-tijdvak voor een datum + afrekenfrequentie.
 *
 * Mapping:
 *   • MAANDELIJKS → MAAND van de datum.
 *   • WEKELIJKS   → WEEK (ISO-week ma-zo) van de datum.
 *   • KWARTAAL    → KWARTAAL van de datum.
 *   • EENMALIG    → MAAND van de datum, maar `eenmalig: true` (een transactie,
 *                   geen herhaling — de generatie-laag mag na de eerste tx geen
 *                   tweede meer maken).
 *   • PER_WERKBON / JAARLIJKS (legacy) → vallen terug op MAAND van de datum.
 *     Deze enum-leden blijven bestaan voor backward-compatibiliteit met oude
 *     regels maar zijn niet meer selecteerbaar in de UI.
 */
export function bepaalPeriodeVoorFrequentie(
  date: Date,
  frequentie: ICAfrekenFrequentie,
): PeriodeVoorFrequentie {
  switch (frequentie) {
    case 'WEKELIJKS': {
      const { jaar, week } = isoWeek(date)
      const range = weekRange(jaar, week)
      return {
        periodetype: 'WEEK',
        jaar,
        maand: null,
        week,
        kwartaal: null,
        periodeVan: range.van,
        periodeTot: range.tot,
        eenmalig: false,
      }
    }
    case 'KWARTAAL': {
      const { jaar, kwartaal } = kwartaalVan(date)
      const range = kwartaalRange(jaar, kwartaal)
      return {
        periodetype: 'KWARTAAL',
        jaar,
        maand: null,
        week: null,
        kwartaal,
        periodeVan: range.van,
        periodeTot: range.tot,
        eenmalig: false,
      }
    }
    case 'EENMALIG': {
      const { jaar, maand } = nlYmd(date)
      const range = maandRange(jaar, maand)
      return {
        periodetype: 'MAAND',
        jaar,
        maand,
        week: null,
        kwartaal: null,
        periodeVan: range.van,
        periodeTot: range.tot,
        eenmalig: true,
      }
    }
    // MAANDELIJKS + legacy PER_WERKBON / JAARLIJKS → maand van de datum.
    case 'MAANDELIJKS':
    case 'PER_WERKBON':
    case 'JAARLIJKS':
    default: {
      const { jaar, maand } = nlYmd(date)
      const range = maandRange(jaar, maand)
      return {
        periodetype: 'MAAND',
        jaar,
        maand,
        week: null,
        kwartaal: null,
        periodeVan: range.van,
        periodeTot: range.tot,
        eenmalig: false,
      }
    }
  }
}

/**
 * Stabiele bucket-sleutel voor een periode-resultaat. Gebruikt om dayEntries van
 * één BV-paar te sub-bucketen per afrekentijdvak (een WEKELIJKSE regel met dagen
 * over twee ISO-weken levert zo twee transacties; een MAANDELIJKSE regel binnen
 * één maand één). Formaat:
 *   MAAND    → "MAAND-2026-06"
 *   WEEK     → "WEEK-2026-25"
 *   KWARTAAL → "KWARTAAL-2026-Q2"
 */
export function periodeKey(p: PeriodeVoorFrequentie): string {
  switch (p.periodetype) {
    case 'WEEK':
      return `WEEK-${p.jaar}-${p.week}`
    case 'KWARTAAL':
      return `KWARTAAL-${p.jaar}-Q${p.kwartaal}`
    case 'MAAND':
    default:
      return `MAAND-${p.jaar}-${String(p.maand).padStart(2, '0')}`
  }
}

/** Aantal ISO-weken in een jaar (re-export voor callers van deze module). */
export { isoWeeksInYear }

/** Volledige NL-maandnamen (1-12), voor `formatPeriodeLabel`. */
const NL_MAANDEN = [
  'Januari',
  'Februari',
  'Maart',
  'April',
  'Mei',
  'Juni',
  'Juli',
  'Augustus',
  'September',
  'Oktober',
  'November',
  'December',
] as const

/**
 * Mensvriendelijk NL-label voor een IC-periode, ongeacht het periodetype.
 *
 * Eén bron voor periode-weergave (API-berichten, export-bestandsnamen, UI). Sinds
 * periodes ook WEEK/KWARTAAL kunnen zijn (waar `maand` null is) mag nergens meer
 * een maand-gebaseerd label gebouwd worden dat "null/2026" of "2026-null" toont.
 *
 * Output (NL, geen em-dash):
 *   MAAND    → "Juni 2026"     (val terug op de maand van `periodeVan` als `maand` ontbreekt)
 *   WEEK     → "Week 23 2026"  (val terug op `jaar` als `week` ontbreekt)
 *   KWARTAAL → "Q2 2026"       (val terug op `jaar` als `kwartaal` ontbreekt)
 *
 * Defensief: een MAAND-periode zonder bruikbare maand valt terug op alleen het
 * jaar zodat er nooit een lege of "undefined" maandnaam verschijnt.
 */
export function formatPeriodeLabel(p: {
  periodetype: ICPeriodetype
  jaar: number
  maand: number | null
  week: number | null
  kwartaal: number | null
  periodeVan?: Date | null
}): string {
  switch (p.periodetype) {
    case 'WEEK':
      return p.week != null ? `Week ${p.week} ${p.jaar}` : `Week ${p.jaar}`
    case 'KWARTAAL':
      return p.kwartaal != null ? `Q${p.kwartaal} ${p.jaar}` : `Kwartaal ${p.jaar}`
    case 'MAAND':
    default: {
      const maand = p.maand ?? (p.periodeVan != null ? nlYmd(p.periodeVan).maand : null)
      if (maand == null || maand < 1 || maand > 12) return `${p.jaar}`
      return `${NL_MAANDEN[maand - 1]} ${p.jaar}`
    }
  }
}

/**
 * Maand-component (1-12) voor de IC-transactie-code `IC-YYYY-MM-NNNN`.
 *
 * `buildICTransactionCode` verwacht een maand; sinds periodes ook WEEK/KWARTAAL
 * kunnen zijn (waar `maand` null is) leiden we voor die periodes de maand af uit
 * de kalendermaand van `periodeVan` (in Europe/Amsterdam). Zo blijven codes
 * welgevormd en chronologisch sorteerbaar. Bij een MAAND-periode wordt `maand`
 * gewoon teruggegeven (byte-identiek aan het oude gedrag).
 *
 * Fallback-volgorde: expliciete `maand` → maand van `periodeVan` → 1.
 */
export function icCodeMaandComponent(periode: {
  maand: number | null
  periodeVan: Date | null
}): number {
  if (periode.maand != null) return periode.maand
  if (periode.periodeVan != null) return nlYmd(periode.periodeVan).maand
  return 1
}
